import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7lrtl5nkhlbktri2sag';

const trustItems = [
  {
    icon: 'ri-fingerprint-line',
    title: 'Kişiye Özel Üretim',
    desc: 'Her ürün yalnızca siz için, sıfırdan tasarlanıp üretilir.',
  },
  {
    icon: 'ri-award-line',
    title: 'Kaliteli İşçilik',
    desc: 'Altın ve gümüş işlemede yıllarca deneyimli ustalar.',
  },
  {
    icon: 'ri-quill-pen-line',
    title: 'Özel Tasarım Süreci',
    desc: 'Sizinle birlikte geliştirilen, onaysız üretilmeyen tasarımlar.',
  },
  {
    icon: 'ri-shield-star-line',
    title: 'Müşteri Memnuniyeti',
    desc: 'Her aşamada yanınızdayız. Sonuç tatmin edene kadar devam.',
  },
];

export default function TrustLeadSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const desc = (data.get('description') as string) || '';
    if (desc.length > 500) return;

    setStatus('sending');
    const params = new URLSearchParams();
    params.append('name', data.get('name') as string);
    params.append('description', desc);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="w-full bg-luxury-dark py-24 md:py-36 px-6 md:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Trust */}
        <div className="text-center mb-20">
          <div className="reveal">
            <span className="font-sans text-gold text-xs tracking-luxury uppercase">
              Neden Sara?
            </span>
          </div>
          <h2 className="reveal reveal-delay-1 font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light mt-5 leading-tight">
            Size Güven Veriyoruz
          </h2>
          <div className="reveal reveal-delay-2 gold-line w-24 mx-auto mt-8" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-28">
          {trustItems.map((item, i) => (
            <div
              key={item.title}
              className={`reveal reveal-delay-${i + 1} luxury-border hover:border-gold/40 transition-all duration-400 p-8 bg-luxury-card flex flex-col items-center text-center gap-5 group`}
            >
              <div className="w-14 h-14 flex items-center justify-center luxury-border rounded-sm group-hover:border-gold/60 transition-colors duration-300">
                <i className={`${item.icon} text-gold text-xl`} />
              </div>
              <div>
                <h3 className="font-sans text-white text-sm font-medium tracking-wide mb-2">
                  {item.title}
                </h3>
                <p className="font-sans text-white/40 text-xs leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Lead Form */}
        <div id="lead-form" className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <div className="reveal">
              <span className="font-sans text-gold text-xs tracking-luxury uppercase">
                Tasarım Talebi
              </span>
            </div>
            <h2 className="reveal reveal-delay-1 font-serif text-3xl md:text-4xl text-white font-light mt-4 leading-snug">
              Hayalinizdeki Takıyı Anlatın
            </h2>
            <p className="reveal reveal-delay-2 font-sans text-white/40 text-sm mt-3 leading-relaxed">
              Formu doldurun, en kısa sürede sizinle iletişime geçelim.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            id="sara-design-request"
            className="reveal luxury-border bg-luxury-card p-8 md:p-10 flex flex-col gap-6"
          >
            {/* Name */}
            <div>
              <label className="font-sans text-gold text-xs tracking-luxury uppercase block mb-3">
                Ad Soyad <span className="text-gold">*</span>
              </label>
              <input
                type="text"
                name="name"
                required
                placeholder="Adınız Soyadınız"
                className="w-full bg-luxury-black border border-luxury-border focus:border-gold/50 text-white placeholder-white/20 text-sm font-sans px-5 py-4 outline-none transition-colors duration-300"
              />
            </div>

            {/* Design description */}
            <div>
              <label className="font-sans text-gold text-xs tracking-luxury uppercase block mb-3">
                Nasıl Bir Tasarım İstiyorsunuz? <span className="text-gold">*</span>
              </label>
              <textarea
                name="description"
                required
                rows={5}
                maxLength={500}
                placeholder="Hayal ettiğiniz takıyı bize anlatın... İsim, harf, bileklik, yüzük gibi detaylar, tercih ettiğiniz malzeme ve özel notlar."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full bg-luxury-black border border-luxury-border focus:border-gold/50 text-white placeholder-white/20 text-sm font-sans px-5 py-4 outline-none transition-colors duration-300 resize-none"
              />
              <p
                className={`font-sans text-xs mt-1 text-right ${
                  charCount > 490 ? 'text-red-400' : 'text-white/30'
                }`}
              >
                {charCount} / 500
              </p>
            </div>

            {/* Image note */}
            <div className="bg-luxury-black/60 border border-luxury-border p-4 flex items-start gap-3">
              <i className="ri-image-line text-gold/60 text-base flex-shrink-0 mt-0.5" />
              <p className="font-sans text-white/40 text-xs leading-relaxed">
                Görsel referans eklemek ister misiniz? WhatsApp üzerinden kolayca paylaşabilirsiniz.
              </p>
            </div>

            {/* Submit */}
            {status === 'success' ? (
              <div className="flex items-center gap-3 bg-gold/10 border border-gold/30 px-6 py-4">
                <i className="ri-check-line text-gold text-lg" />
                <p className="font-sans text-white/80 text-sm">
                  Talebiniz alındı! En kısa sürede sizinle iletişime geçeceğiz.
                </p>
              </div>
            ) : (
              <>
                {status === 'error' && (
                  <p className="font-sans text-red-400 text-xs text-center">
                    Bir hata oluştu. Lütfen WhatsApp üzerinden bize yazın.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="btn-gold w-full py-4 text-xs rounded-sm whitespace-nowrap flex items-center justify-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      Talebimi Gönder
                    </>
                  )}
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
